import { useCallback, useEffect, useMemo, useRef } from "react";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import type { BunkerBargeDetailsPayload } from "@services/vessel/types";
import { updateBunkerBarge } from "@services/vesselsApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { normalizeDate, today } from "@utils/date-time";
import { toDate, toISODateStrict } from "@utils/formatters/form";
import { paths } from "@utils/navigation";
import type { AxiosError } from "axios";
import type { TFunction } from "i18next";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useCompleteTask } from "../hooks/useCompleteTask";
import { LANG_BASE, TASK_STATUS, TEST_ID_BASE } from "../VesselStatus";

const action = Object.freeze({
  UPDATE: "UPDATE_BB",
  APPROVE: "APPROVE_BB",
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  vesselId: string;
  isUpdate: boolean;
  startDate?: string;
  endDate?: string;
  gracePeriod?: string;
  remarks?: string;
  internalNotes?: string;
  internalNotesDocument?: string;
  internalNotesDocumentName?: string;
  externalNotes?: string;
  externalNotesDocument?: string;
  externalNotesDocumentName?: string;
  version: number;
};

const FORM_FIELDS = {
  FROM_DATE: "fromDate",
  TO_DATE: "toDate",
  GRACE_PERIOD: "gracePeriod",
  REMARKS: "remarks",
  INTERNAL_NOTES: "internalNotes",
  EXTERNAL_NOTES: "externalNotes",
  APPROVE_BUNKER_BARGE_ATTACHMENTS: "approveBunkerBargeAttachments",
  APPROVE_BUNKER_BARGE_ATTACHMENTS_NAME: "approveBunkerBargeAttachmentsName",
  UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS: "updateBunkerBargeInternalAttachments",
  UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS_NAME: "updateBunkerBargeInternalAttachmentsName",
  UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS: "updateBunkerBargeExternalAttachments",
  UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS_NAME: "updateBunkerBargeExternalAttachmentsName",
} as const;

type AttachmentField =
  | typeof FORM_FIELDS.APPROVE_BUNKER_BARGE_ATTACHMENTS
  | typeof FORM_FIELDS.UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS
  | typeof FORM_FIELDS.UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS;

type AttachmentNameField =
  | typeof FORM_FIELDS.APPROVE_BUNKER_BARGE_ATTACHMENTS_NAME
  | typeof FORM_FIELDS.UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS_NAME
  | typeof FORM_FIELDS.UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS_NAME;

type BunkerBargeFormValues = {
  fromDate: Date | null;
  toDate?: Date | null;
  gracePeriod?: Date | null;
  remarks?: string;
  internalNotes?: string;
  externalNotes?: string;
  approveBunkerBargeAttachments?: string;
  approveBunkerBargeAttachmentsName?: string;
  updateBunkerBargeInternalAttachments?: string;
  updateBunkerBargeInternalAttachmentsName?: string;
  updateBunkerBargeExternalAttachments?: string;
  updateBunkerBargeExternalAttachmentsName?: string;
};

type UploadDocumentVariables = {
  file: File;
  documentType: string;
  transactionId?: string;
};

type UploadDocumentResult = {
  documentUrl: string;
  documentType: string;
};

const UPDATE_BUNKER_BARGE_TRANSACTION_CONTEXT = {
  module: "vessel_registration",
  screen: "Vessel Status",
  action: "update_bunker_barge",
  entityType: "vessel",
};

const UPLOAD_BUNKER_BARGE_ATTACHMENT_TRANSACTION_CONTEXT = {
  module: "vessel_registration",
  screen: "Vessel Status",
  action: "upload_bunker_barge_attachment",
  entityType: "document",
};

const getApproveUpdateBunkerBargeSchema = (t: TFunction, isFromDateDisabled = false) =>
  z
    .object({
      [FORM_FIELDS.FROM_DATE]: z
        .union([z.date(), z.string(), z.null()])
        .transform((val) => toDate(val)),
      [FORM_FIELDS.TO_DATE]: z
        .union([z.date(), z.string(), z.null()])
        .transform((val) => toDate(val))
        .optional(),
      [FORM_FIELDS.GRACE_PERIOD]: z
        .union([z.date(), z.string(), z.null()])
        .transform((val) => toDate(val))
        .optional(),
      [FORM_FIELDS.REMARKS]: z
        .string()
        .max(
          1500,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.remarks`),
            max: 1500,
          }),
        )
        .optional(),
      [FORM_FIELDS.INTERNAL_NOTES]: z
        .string()
        .max(
          1000,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.delegation-reason`),
            max: 1000,
          }),
        )
        .optional(),
      [FORM_FIELDS.EXTERNAL_NOTES]: z
        .string()
        .max(
          1000,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.delegation-reason`),
            max: 1000,
          }),
        )
        .optional(),
      [FORM_FIELDS.APPROVE_BUNKER_BARGE_ATTACHMENTS]: z.string().optional(),
      [FORM_FIELDS.APPROVE_BUNKER_BARGE_ATTACHMENTS_NAME]: z.string().optional(),
      [FORM_FIELDS.UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS]: z.string().optional(),
      [FORM_FIELDS.UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS_NAME]: z.string().optional(),
      [FORM_FIELDS.UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS]: z.string().optional(),
      [FORM_FIELDS.UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS_NAME]: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const fromDate = normalizeDate(data.fromDate);
      const toDate = normalizeDate(data.toDate);
      const gracePeriod = normalizeDate(data.gracePeriod);
      const today = normalizeDate(new Date()) as Date;

      const fromLabel = t(`${LANG_BASE}.start-date`);
      const toLabel = t(`${LANG_BASE}.end-date`);
      const graceLabel = t(`${LANG_BASE}.grace-period`);

      if (!fromDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.FROM_DATE],
          message: t("form-validation.required", { label: fromLabel }),
        });
      }

      if (!isFromDateDisabled && fromDate && fromDate < today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.FROM_DATE],
          message: t("form-validation.must-not-be-in-past", { label: fromLabel }),
        });
      }

      if (toDate && toDate < today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.TO_DATE],
          message: t("form-validation.must-not-be-in-past", { label: toLabel }),
        });
      }

      if (fromDate && toDate && toDate <= fromDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.TO_DATE],
          message: t("form-validation.must-be-after", {
            subject: toLabel,
            object: fromLabel,
          }),
        });
      }

      if (gracePeriod && toDate && gracePeriod <= toDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.GRACE_PERIOD],
          message: t("form-validation.must-be-after", {
            subject: graceLabel,
            object: toLabel,
          }),
        });
      }
    });

const uploadDocument = async ({
  file,
  documentType,
  transactionId,
}: UploadDocumentVariables): Promise<UploadDocumentResult> => {
  const payloadForSasToken = {
    prefix: SasTokenPrefix.VESSEL_REGISTRATION,
    contentType: file.type || null,
    privilege: SasTokenPrivilege.WRITE,
    blobUrl: null,
  };

  const { blobUrl, sasToken } = await getSasToken(
    payloadForSasToken,
    ...(transactionId ? [transactionId] : []),
  );

  const [uploadResult] = await uploadFileToAzureBlob({
    file,
    blobUrl,
    sasToken,
    certificateType: documentType,
  });

  const documentUrl = uploadResult?.documentUrl ?? blobUrl;
  return { documentUrl, documentType };
};

const isBeforeToday = (value?: string) => {
  const date = normalizeDate(value);

  return Boolean(date && date <= today());
};

const toUploadEntry = (url?: string, name?: string): UploadEntry[] =>
  url
    ? [
        {
          name: name || "attachment",
          url,
        },
      ]
    : [];

const ApproveUpdateBunkerBargeSideDrawer = ({
  isOpen,
  onClose,
  vesselId,
  isUpdate,
  startDate,
  endDate,
  gracePeriod,
  remarks,
  internalNotes,
  internalNotesDocument,
  internalNotesDocumentName,
  externalNotes,
  externalNotesDocument,
  externalNotesDocumentName,
  version,
}: Props) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const { clearFilesForKey, setFilesForKey, filesByKey } = useUploadStore();

  const internalAttachmentsField = isUpdate
    ? FORM_FIELDS.UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS
    : FORM_FIELDS.APPROVE_BUNKER_BARGE_ATTACHMENTS;
  const internalAttachmentsNameField = isUpdate
    ? FORM_FIELDS.UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS_NAME
    : FORM_FIELDS.APPROVE_BUNKER_BARGE_ATTACHMENTS_NAME;
  const externalAttachmentsField = FORM_FIELDS.UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS;
  const externalAttachmentsNameField = FORM_FIELDS.UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS_NAME;

  const internalAttachments = filesByKey[internalAttachmentsField] || [];
  const externalAttachments = filesByKey[externalAttachmentsField] || [];

  const uploadConditions = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = uploadConditions.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const isFromDateDisabled = isUpdate && isBeforeToday(startDate);
  const initializationKey = `${isUpdate ? "update" : "approve"}:${vesselId}:${version}`;
  const initializedKeyRef = useRef<string | null>(null);
  const formDefaultValues = useMemo(
    () => ({
      [FORM_FIELDS.FROM_DATE]: startDate ?? null,
      [FORM_FIELDS.TO_DATE]: endDate ?? null,
      [FORM_FIELDS.GRACE_PERIOD]: isUpdate ? (gracePeriod ?? null) : null,
      [FORM_FIELDS.REMARKS]: remarks ?? "",
      [FORM_FIELDS.INTERNAL_NOTES]: isUpdate ? (internalNotes ?? "") : "",
      [FORM_FIELDS.EXTERNAL_NOTES]: isUpdate ? (externalNotes ?? "") : "",
      [FORM_FIELDS.APPROVE_BUNKER_BARGE_ATTACHMENTS]: "",
      [FORM_FIELDS.APPROVE_BUNKER_BARGE_ATTACHMENTS_NAME]: "",
      [FORM_FIELDS.UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS]: isUpdate
        ? (internalNotesDocument ?? "")
        : "",
      [FORM_FIELDS.UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS_NAME]: isUpdate
        ? (internalNotesDocumentName ?? "")
        : "",
      [FORM_FIELDS.UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS]: isUpdate
        ? (externalNotesDocument ?? "")
        : "",
      [FORM_FIELDS.UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS_NAME]: isUpdate
        ? (externalNotesDocumentName ?? "")
        : "",
    }),
    [
      endDate,
      externalNotes,
      externalNotesDocument,
      externalNotesDocumentName,
      gracePeriod,
      internalNotes,
      internalNotesDocument,
      internalNotesDocumentName,
      isUpdate,
      remarks,
      startDate,
    ],
  );

  const form = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(getApproveUpdateBunkerBargeSchema(t, isFromDateDisabled)),
    shouldUnregister: false,
    defaultValues: formDefaultValues,
  });

  const fromDate = form.watch(FORM_FIELDS.FROM_DATE, startDate);
  const toDate = form.watch(FORM_FIELDS.TO_DATE, endDate);

  const clearUploadState = useCallback(() => {
    clearFilesForKey(internalAttachmentsField);

    if (isUpdate) {
      clearFilesForKey(externalAttachmentsField);
    }
  }, [clearFilesForKey, externalAttachmentsField, internalAttachmentsField, isUpdate]);

  const handleClose = useCallback(() => {
    initializedKeyRef.current = null;
    form.reset(formDefaultValues);
    clearUploadState();
    onClose();
  }, [clearUploadState, form, formDefaultValues, onClose]);

  const handleSuccess = useCallback(() => {
    handleClose();
    navigate(`/${paths.vesselManagement.index}`);
  }, [handleClose, navigate]);

  useEffect(() => {
    if (!isOpen || initializedKeyRef.current === initializationKey) return;

    initializedKeyRef.current = initializationKey;
    form.reset(formDefaultValues);
    clearUploadState();

    if (!isUpdate) return;

    form.setValue(
      FORM_FIELDS.UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS,
      internalNotesDocument ?? "",
    );
    form.setValue(
      FORM_FIELDS.UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS_NAME,
      internalNotesDocumentName ?? "",
    );
    form.setValue(
      FORM_FIELDS.UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS,
      externalNotesDocument ?? "",
    );
    form.setValue(
      FORM_FIELDS.UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS_NAME,
      externalNotesDocumentName ?? "",
    );
    setFilesForKey(
      FORM_FIELDS.UPDATE_BUNKER_BARGE_INTERNAL_ATTACHMENTS,
      toUploadEntry(internalNotesDocument, internalNotesDocumentName),
    );
    setFilesForKey(
      FORM_FIELDS.UPDATE_BUNKER_BARGE_EXTERNAL_ATTACHMENTS,
      toUploadEntry(externalNotesDocument, externalNotesDocumentName),
    );
  }, [
    clearUploadState,
    externalNotesDocument,
    externalNotesDocumentName,
    form,
    formDefaultValues,
    initializationKey,
    internalNotesDocument,
    internalNotesDocumentName,
    isOpen,
    isUpdate,
    setFilesForKey,
  ]);

  const { mutate: completeTask, isPending: isCompletingTask } = useCompleteTask({ handleSuccess });

  const { mutate: updateBunkerBargeMutate, isPending: isUpdatingBunkerBarge } = useMutation({
    mutationFn: ({
      vesselId,
      bunkerBargePayload,
    }: {
      vesselId: string;
      bunkerBargePayload: BunkerBargeDetailsPayload;
    }) =>
      runTransaction(
        {
          ...UPDATE_BUNKER_BARGE_TRANSACTION_CONTEXT,
          entityId: vesselId,
        },
        (transactionId) =>
          updateBunkerBarge({
            service: "bff-vessel",
            id: vesselId,
            bunkerBargePayload,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: () => {
      notify({
        title: isUpdate
          ? t("vessel-management.notifications.vessel-updated-success-title")
          : t("vessel-management.notifications.vessel-approved-success-title"),
        description: "",
        type: "success",
      });
      handleSuccess();
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      notify({
        title: isUpdate
          ? t("vessel-management.notifications.vessel-updated-error-title")
          : t("vessel-management.notifications.vessel-approved-error-title"),
        description: error.response?.data?.detail ?? error.message,
        type: "error",
      });
    },
  });

  const internalAttachmentsUpload = useMutation({
    mutationFn: (variables: UploadDocumentVariables) =>
      runTransaction(UPLOAD_BUNKER_BARGE_ATTACHMENT_TRANSACTION_CONTEXT, (transactionId) =>
        uploadDocument({ ...variables, ...(transactionId ? { transactionId } : {}) }),
      ),
  });

  const externalAttachmentsUpload = useMutation({
    mutationFn: (variables: UploadDocumentVariables) =>
      runTransaction(UPLOAD_BUNKER_BARGE_ATTACHMENT_TRANSACTION_CONTEXT, (transactionId) =>
        uploadDocument({ ...variables, ...(transactionId ? { transactionId } : {}) }),
      ),
  });

  const handleAttachmentUpload = useCallback(
    (
      files: File[],
      documentType: string,
      attachmentField: AttachmentField,
      attachmentNameField: AttachmentNameField,
      uploadMutation: typeof internalAttachmentsUpload,
    ) => {
      const file = files[0];
      if (!file) return;

      uploadMutation.mutate(
        { file, documentType },
        {
          onSuccess: ({ documentUrl }) => {
            form.setValue(attachmentField, documentUrl);
            form.setValue(attachmentNameField, file.name);
            setFilesForKey(attachmentField, [
              {
                name: file.name,
                url: documentUrl,
              },
            ]);
          },
          onError: () => {
            clearFilesForKey(attachmentField);
            notify({
              title: t("vessel-management.notifications.attachments-upload-error"),
              description: t(
                "vessel-management.notifications.attachments-upload-error-description",
              ),
              type: "error",
            });
          },
        },
      );
    },
    [clearFilesForKey, form, notify, setFilesForKey, t],
  );

  const handleInternalFileUpload = useCallback(
    (files: File[]) => {
      handleAttachmentUpload(
        files,
        "INTERNAL_NOTES",
        internalAttachmentsField,
        internalAttachmentsNameField,
        internalAttachmentsUpload,
      );
    },
    [
      handleAttachmentUpload,
      internalAttachmentsField,
      internalAttachmentsNameField,
      internalAttachmentsUpload,
    ],
  );

  const handleExternalFileUpload = useCallback(
    (files: File[]) => {
      handleAttachmentUpload(
        files,
        "EXTERNAL_NOTES",
        externalAttachmentsField,
        externalAttachmentsNameField,
        externalAttachmentsUpload,
      );
    },
    [
      externalAttachmentsField,
      externalAttachmentsNameField,
      externalAttachmentsUpload,
      handleAttachmentUpload,
    ],
  );

  const handleRemoveInternalFile = useCallback(() => {
    form.setValue(internalAttachmentsField, "");
    form.setValue(internalAttachmentsNameField, "");
    clearFilesForKey(internalAttachmentsField);
  }, [clearFilesForKey, form, internalAttachmentsField, internalAttachmentsNameField]);

  const handleRemoveExternalFile = useCallback(() => {
    form.setValue(externalAttachmentsField, "");
    form.setValue(externalAttachmentsNameField, "");
    clearFilesForKey(externalAttachmentsField);
  }, [clearFilesForKey, externalAttachmentsField, externalAttachmentsNameField, form]);

  const onValid = async (values: BunkerBargeFormValues) => {
    const {
      fromDate,
      toDate,
      gracePeriod,
      remarks,
      internalNotes,
      externalNotes,
      approveBunkerBargeAttachments,
      approveBunkerBargeAttachmentsName,
      updateBunkerBargeInternalAttachments,
      updateBunkerBargeInternalAttachmentsName,
      updateBunkerBargeExternalAttachments,
      updateBunkerBargeExternalAttachmentsName,
    } = values;

    if (isUpdate) {
      updateBunkerBargeMutate({
        vesselId,
        bunkerBargePayload: {
          bunkerBargeId: vesselId,
          version,
          effectiveStartDate: fromDate ? toISODateStrict(fromDate) : "",
          effectiveEndDate: toDate ? toISODateStrict(toDate) : "",
          gracePeriod: gracePeriod ? toISODateStrict(gracePeriod) : "",
          remarks: remarks ?? "",
          internalNotes: internalNotes ?? "",
          externalNotes: externalNotes ?? "",
          internalNotesDocument: updateBunkerBargeInternalAttachments ?? "",
          internalNotesDocumentName: updateBunkerBargeInternalAttachmentsName ?? "",
          externalNotesDocument: updateBunkerBargeExternalAttachments ?? "",
          externalNotesDocumentName: updateBunkerBargeExternalAttachmentsName ?? "",
          action: action.UPDATE,
        },
      });

      return;
    }

    completeTask({
      service: "bff-vessel",
      vesselId,
      params: {
        version,
        status: TASK_STATUS.APPROVED,
        effectiveStartDate: fromDate ? toISODateStrict(fromDate) : "",
        effectiveEndDate: toDate ? toISODateStrict(toDate) : "",
        gracePeriod: gracePeriod ? toISODateStrict(gracePeriod) : "",
        remarks,
        internalNotes,
        internalNotesDocument: approveBunkerBargeAttachments,
        internalNotesDocumentName: approveBunkerBargeAttachmentsName,
      },
      successTitle: t("vessel-management.notifications.vessel-approved-success-title"),
      errorTitle: t("vessel-management.notifications.vessel-approved-error-title"),
    });
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  const renderAttachmentUpload = ({
    attachments,
    field,
    label,
    uploadTestId,
    singleFileTestId,
    onUpload,
    onRemove,
    isLoading,
  }: {
    attachments: UploadEntry[];
    field: AttachmentField;
    label: string;
    uploadTestId: string;
    singleFileTestId: string;
    onUpload: (files: File[]) => void;
    onRemove: () => void;
    isLoading: boolean;
  }) =>
    attachments[0] ? (
      <div className="MuiFormControl-root MuiFormControl-fullWidth css-kjxwpk-MuiFormControl-root">
        <CustomFormLabel sx={{ mb: 1 }}>{label}</CustomFormLabel>
        <SingleFile
          testId={singleFileTestId}
          file={attachments[0]}
          fieldName={attachments[0].name}
          currentFileIndex={0}
          setFiles={onRemove}
          isLoading={isLoading}
        />
      </div>
    ) : (
      <DocumentUpload
        name={field}
        label={label}
        testId={uploadTestId}
        allowedExtensions={allowedSideDrawerExtensions}
        conditions={uploadConditions}
        maxFiles={1}
        onUpload={onUpload}
        setFiles={(next) => setFilesForKey(field, next as UploadEntry[])}
        files={attachments}
      />
    );

  return (
    <FormProvider {...form}>
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        onCancel={handleClose}
        onSubmit={onSubmit}
        keepMounted={false}
        title={t(isUpdate ? `${LANG_BASE}.update-bunker-barge-title` : `${LANG_BASE}.approveTitle`)}
        description={t(
          isUpdate
            ? `${LANG_BASE}.update-bunker-barge-description`
            : `${LANG_BASE}.approve-bunker-barge-description`,
        )}
        submitLabel={t(isUpdate ? `${LANG_BASE}.update` : `${LANG_BASE}.approve`)}
        isSubmitting={isUpdate ? isUpdatingBunkerBarge : isCompletingTask}
        width={"400px"}
      >
        <div className="p-6">
          <FormItem
            name={FORM_FIELDS.FROM_DATE}
            type="date-picker"
            label={t(`${LANG_BASE}.start-date`)}
            testId={`${TEST_ID_BASE}-from-date`}
            timePicker={false}
            dateConstraint={isFromDateDisabled ? undefined : "not-past"}
            disabled={isFromDateDisabled}
            onFieldChange={() => {
              form.trigger([FORM_FIELDS.FROM_DATE, FORM_FIELDS.TO_DATE]);
            }}
          />
          <FormItem
            name={FORM_FIELDS.TO_DATE}
            type="date-picker"
            label={t(`${LANG_BASE}.end-date`)}
            testId={`${TEST_ID_BASE}-to-date`}
            timePicker={false}
            minDate={
              fromDate
                ? (() => {
                    const d = new Date(fromDate);
                    d.setDate(d.getDate() + 1);
                    return d;
                  })()
                : undefined
            }
            dateConstraint="not-past"
            onFieldChange={(_, v) => {
              if (!v) {
                form.setValue(FORM_FIELDS.GRACE_PERIOD, null);
              }
              form.trigger([FORM_FIELDS.TO_DATE, FORM_FIELDS.GRACE_PERIOD]);
            }}
          />
          <FormItem
            name={FORM_FIELDS.GRACE_PERIOD}
            type="date-picker"
            label={t(`${LANG_BASE}.grace-period`)}
            testId={`${TEST_ID_BASE}-grace-period`}
            timePicker={false}
            minDate={
              toDate
                ? (() => {
                    const d = new Date(toDate);
                    d.setDate(d.getDate() + 1);
                    return d;
                  })()
                : undefined
            }
            onFieldChange={() => {
              form.trigger(FORM_FIELDS.GRACE_PERIOD);
            }}
            disabled={!toDate}
          />
          <div className={isUpdate ? "flex flex-col gap-6" : undefined}>
            <FormItem
              name={FORM_FIELDS.REMARKS}
              type="textarea"
              label={t(`${LANG_BASE}.remarks`)}
              testId={`${TEST_ID_BASE}-remarks`}
              minRows={4}
              maxChars={1500}
              showCharCounter
            />
            {isUpdate && (
              <>
                <FormItem
                  name={FORM_FIELDS.EXTERNAL_NOTES}
                  type="textarea"
                  label={t(`${LANG_BASE}.comments`)}
                  testId={`${TEST_ID_BASE}-external-notes`}
                  minRows={4}
                  maxChars={1000}
                  showCharCounter
                />
                {renderAttachmentUpload({
                  attachments: externalAttachments,
                  field: externalAttachmentsField,
                  label: t(`${LANG_BASE}.externalAttachments`),
                  uploadTestId: `${TEST_ID_BASE}-external-attachments-upload`,
                  singleFileTestId: `${TEST_ID_BASE}-external-attachment`,
                  onUpload: handleExternalFileUpload,
                  onRemove: handleRemoveExternalFile,
                  isLoading: externalAttachmentsUpload.isPending,
                })}
              </>
            )}
            <FormItem
              name={FORM_FIELDS.INTERNAL_NOTES}
              type="textarea"
              label={t(`${LANG_BASE}.internalNotes`)}
              testId={`${TEST_ID_BASE}-internal-notes`}
              minRows={4}
              maxChars={1000}
              showCharCounter
            />
            {renderAttachmentUpload({
              attachments: internalAttachments,
              field: internalAttachmentsField,
              label: t(`${LANG_BASE}.internalAttachments`),
              uploadTestId: isUpdate
                ? `${TEST_ID_BASE}-internal-attachments-upload`
                : `${TEST_ID_BASE}-approve-bunker-barge-attachments`,
              singleFileTestId: isUpdate
                ? `${TEST_ID_BASE}-internal-attachment`
                : `${TEST_ID_BASE}-attachment-0`,
              onUpload: handleInternalFileUpload,
              onRemove: handleRemoveInternalFile,
              isLoading: internalAttachmentsUpload.isPending,
            })}
          </div>
        </div>
      </SideDrawer>
    </FormProvider>
  );
};

export default ApproveUpdateBunkerBargeSideDrawer;
